import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { AuthShell, inputClass } from "@/pages/authShared";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If an account exists, a 6-digit code has been emailed.");
      setStep(2);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError("Password must be at least 8 characters and include a letter and a number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, password });
      toast.success("Password updated! Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || "Reset failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Reset password" subtitle={step === 1 ? "We'll email you a code" : "Enter the code we sent"}>
      {step === 1 ? (
        <form onSubmit={requestCode} className="space-y-3">
          <input className={inputClass} type="email" placeholder="Your account email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="fp-email" required />
          <button type="submit" disabled={loading} data-testid="fp-request" className="w-full bg-[#0B7A3B] text-white font-bold rounded-full py-3 active:scale-95 transition-transform hover:bg-[#095F2E] disabled:opacity-60">
            {loading ? "Sending…" : "Send reset code"}
          </button>
        </form>
      ) : (
        <form onSubmit={doReset} className="space-y-3">
          <input className={`${inputClass} tracking-[6px] text-center font-bold`} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="fp-code" maxLength={6} required />
          <input className={inputClass} type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="fp-newpass" minLength={8} required />
          <ul className="space-y-1 px-1 text-xs" data-testid="fp-password-hints">
            {[
              { ok: password.length >= 8, label: "At least 8 characters" },
              { ok: /[A-Za-z]/.test(password), label: "Contains a letter" },
              { ok: /\d/.test(password), label: "Contains a number" },
            ].map((r) => (
              <li key={r.label} className={`flex items-center gap-1.5 font-semibold ${r.ok ? "text-[#0B7A3B]" : "text-[#6B8375]"}`}>
                <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] leading-none border" style={{ borderColor: r.ok ? "#0B7A3B" : "#6B8375" }}>{r.ok ? "✓" : ""}</span>
                {r.label}
              </li>
            ))}
          </ul>
          {error && (
            <div className="text-sm font-semibold text-[#C0392B] bg-[#C0392B]/10 rounded-[12px] px-3 py-2.5" data-testid="fp-error">{error}</div>
          )}
          <button type="submit" disabled={loading} data-testid="fp-reset" className="w-full bg-[#0B7A3B] text-white font-bold rounded-full py-3 active:scale-95 transition-transform hover:bg-[#095F2E] disabled:opacity-60">
            {loading ? "Updating…" : "Set new password"}
          </button>
          <button type="button" onClick={() => setStep(1)} className="w-full text-sm font-semibold text-[#6B8375]">
            Didn't get a code? Try again
          </button>
        </form>
      )}
      <p className="text-center text-sm text-[#6B8375] mt-5">
        <Link to="/login" className="font-bold text-[#0B7A3B]" data-testid="fp-back-login">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
